import React from 'react';
import useServerSpeechToText, { serverDictationSupported } from './useServerSpeechToText.js';
import { USE_BROWSER_STT } from '../lib/runtimeEnv.js';

// Voice → text for dictating a field the dentist would otherwise type
// (شکایت اصلی, یادداشت, and the visit narrative in تشخیص).
//
// TWO ENGINES LIVE HERE. The default export is the dispatcher; which one it
// returns is decided once, below, and no caller has to know which it got.
//
// ── 2026-08-25: THE DEFAULT MOVED TO THE SERVER, BECAUSE OF SAFARI ─────────
// This file used to be the browser engine and nothing else, and its own comment
// said the replacement, if the position ever changed, would be "a BFF mutation
// behind this same hook and no caller has to change". That is what happened,
// and the reason was not privacy in the end — it was that the engine DOES NOT
// EXIST on the device dentists use.
//
// `webkitSpeechRecognition` is Chrome and Edge. Safari does not implement it,
// Firefox does not implement it, and neither does any browser on iOS — they are
// all WebKit underneath, so an iPad has no recogniser no matter what is
// installed on it. `supported` was therefore false on the iPad a dentist holds
// chairside, and VoiceDictateButton correctly rendered nothing at all: the
// feature simply did not exist on half the devices it was written for.
//
// The server path records with MediaRecorder — present in every one of those
// browsers — and streams the audio to parsina.bff.dentist's /ws/transcribe.
// See useServerSpeechToText.js.
//
// Two things changed with it, and they pull in opposite directions:
//   * PRIVACY IMPROVED. The browser engine was never local recognition: Chrome
//     and Edge stream the microphone to GOOGLE'S servers, so a patient's spoken
//     complaint was leaving this deployment. It now reaches Parsina's own AI
//     service over the BFF's authenticated socket.
//   * IT COSTS پارسه, where the browser engine was free. `USE_BROWSER_STT=true`
//     buys the old behaviour back, at the price of no microphone on iPads.

// ── The browser engine ──────────────────────────────────────────────────────

const Engine =
  (typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
  null;

/** Whether the BROWSER engine exists. Not "whether dictation works" — see
 *  `dictationSupported`, which is the question a caller usually means. */
export const speechSupported = !!Engine;

// Recognition wants a BCP-47 tag; the app's locale is the bare 'fa' | 'en' | 'fr' | 'de'.
// Every SUPPORTED locale needs a row: a missing one listens for PERSIAN.
const TAGS = { fa: 'fa-IR', en: 'en-US', fr: 'fr-FR', de: 'de-DE' };

/**
 * @param {object}   opts
 * @param {string}   opts.locale     app locale ('fa' | 'en' | 'fr' | 'de')
 * @param {Function} opts.onResult   called with each FINAL chunk of speech
 * @param {boolean}  opts.continuous keep listening across pauses until stopped.
 *   OFF suits a one-line field (شکایت اصلی): the session closes itself at the
 *   first natural pause and the dentist never has to press stop. ON suits a
 *   paragraph (یادداشت), where every breath would otherwise end the recording
 *   and the dentist would be pressing the mic once per sentence.
 */
export function useBrowserSpeechToText({ locale = 'fa', onResult, continuous = false } = {}) {
  const [listening, setListening] = React.useState(false);
  const [interim, setInterim] = React.useState('');
  const [error, setError] = React.useState(null);
  const recRef = React.useRef(null);

  // onResult is read at event time, so a caller may pass a fresh closure every
  // render without tearing down the recogniser mid-sentence.
  const cb = React.useRef(onResult);
  React.useEffect(() => { cb.current = onResult; }, [onResult]);

  const stop = React.useCallback(() => {
    const rec = recRef.current;
    recRef.current = null;
    setListening(false);
    setInterim('');
    if (rec) {
      // Detach first: `stop()` fires onend, which would otherwise re-enter this
      // and null a recogniser the next start() has already installed.
      rec.onresult = null; rec.onerror = null; rec.onend = null;
      try { rec.stop(); } catch { /* already dead */ }
    }
  }, []);

  const start = React.useCallback(() => {
    if (!Engine || recRef.current) return;
    setError(null);
    setInterim('');

    const rec = new Engine();
    rec.lang = TAGS[locale] || TAGS.fa;
    rec.continuous = continuous;
    // interimResults gives the dentist live feedback that the microphone is
    // actually hearing them, rather than a dead button until the first pause.
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let pending = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const text = r[0]?.transcript || '';
        if (r.isFinal) cb.current?.(text.trim());
        else pending += text;
      }
      setInterim(pending);
    };

    rec.onerror = (event) => {
      // `aborted` is what stop() itself raises, and `no-speech` is a silent
      // room — neither is a failure worth showing a dentist mid-visit.
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        // Normalised to the SAME vocabulary the server engine reports, so
        // VoiceDictateButton renders one set of messages and never has to ask
        // which engine produced the failure.
        setError(event.error === 'not-allowed' ? 'denied' : 'generic');
      }
      stop();
    };

    // Chrome ends a `continuous` session on its own after a long silence. That
    // is left to stand rather than auto-restarted: a recogniser that respawns
    // itself is a microphone that never closes, which is not a thing to leave
    // running in a room where patients are talking.
    rec.onend = () => stop();

    recRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      setError('generic');
      stop();
    }
  }, [locale, continuous, stop]);

  const toggle = React.useCallback(() => {
    if (recRef.current) stop(); else start();
  }, [start, stop]);


  // A recogniser left running past unmount keeps the tab's mic indicator lit.
  React.useEffect(() => stop, [stop]);

  return {
    listening,
    // The browser engine transcribes as it hears; there is no post-recording
    // wait to render. Present so both engines return the same shape.
    transcribing: false,
    interim,
    error,
    supported: speechSupported,
    start,
    stop,
    toggle,
  };
}

// ── The dispatcher ──────────────────────────────────────────────────────────

/**
 * Is dictation possible AT ALL on this device, by either route?
 *
 * This is what an affordance should be hidden on, not `speechSupported`: the
 * question is whether pressing a microphone can produce words, not which engine
 * would produce them.
 */
export function dictationSupported() {
  return USE_BROWSER_STT ? speechSupported : serverDictationSupported();
}

/**
 * Both hooks are called on every render — the rules of hooks require it — and
 * only the chosen one is returned. The unchosen one holds no microphone and
 * opens no socket until its `start()` is called, so this costs two `useState`
 * sets and nothing else.
 *
 * `engine` is returned because ONE thing does legitimately differ for a
 * dentist: the browser engine stops itself at a natural pause, and the server
 * engine records until they say stop. The instruction on screen has to tell the
 * truth about which one they are using.
 */
export default function useSpeechToText(opts = {}) {
  const browser = useBrowserSpeechToText(opts);
  const server = useServerSpeechToText(opts);

  const useBrowser = USE_BROWSER_STT && speechSupported;
  return { ...(useBrowser ? browser : server), engine: useBrowser ? 'browser' : 'server' };
}
